import React from 'react';
import { createPortal } from 'react-dom';
import './LibraryTableHead.css';

type Dock = {
  top: number;
  left: number;
  width: number;
  tableLeft: number;
  tableWidth: number;
  columns: number[];
  contextClass: string;
  tableClass: string;
};

/** A live React copy of the column controls, aligned to the scrolled source table. */
export default function LibraryTableHead({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  const headRef = React.useRef<HTMLTableSectionElement>(null);
  const [dock, setDock] = React.useState<Dock | null>(null);

  React.useLayoutEffect(() => {
    const head = headRef.current;
    const table = head?.closest('table');
    if (!head || !table) return;
    const page = table.closest<HTMLElement>('.library-page');
    let frame = 0;
    const measure = () => {
      frame = 0;
      const header = head.getBoundingClientRect();
      const body = table.getBoundingClientRect();
      let left = Math.max(0, body.left);
      let right = Math.min(document.documentElement.clientWidth, body.right);
      // Preserve horizontal clipping without turning the page into a second scroll owner.
      for (let parent = table.parentElement; parent; parent = parent.parentElement) {
        if (/auto|scroll|hidden|clip/.test(getComputedStyle(parent).overflowX)) {
          const bounds = parent.getBoundingClientRect();
          left = Math.max(left, bounds.left + parent.clientLeft);
          right = Math.min(right, bounds.left + parent.clientLeft + parent.clientWidth);
        }
      }
      let top = 0;
      const bar = document.querySelector<HTMLElement>('.topbar');
      if (bar) {
        const bounds = bar.getBoundingClientRect();
        if (bounds.right > left && bounds.left < right) top = Math.max(0, bounds.bottom - 1);
      }
      page?.querySelectorAll<HTMLElement>('.library-toolbar-block[data-melted="true"]').forEach(toolbar => {
        resize.observe(toolbar);
        const bounds = toolbar.getBoundingClientRect();
        if (bounds.right > left && bounds.left < right) top = Math.max(top, bounds.bottom);
      });
      // A hidden card-mode header has no geometry. At a group's end let the next group take over.
      if (!head.isConnected || header.height === 0 || right <= left || header.top >= top
        || body.bottom <= top + header.height || getComputedStyle(head).display === 'none') {
        setDock(current => current === null ? current : null);
        return;
      }
      const cells = Array.from(head.rows[0]?.cells ?? []);
      const context = table.closest<HTMLElement>('.venues-list-view,.clients-list-view,.events-list-view,.service-ticket-table-wrap');
      const next: Dock = {
        top, left, width: right - left, tableLeft: body.left - left, tableWidth: body.width,
        columns: cells.map(cell => cell.getBoundingClientRect().width),
        contextClass: context?.className ?? '', tableClass: table.className,
      };
      setDock(current => current && JSON.stringify(current) === JSON.stringify(next) ? current : next);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    const resize = new ResizeObserver(schedule);
    resize.observe(table);
    resize.observe(head);
    if (page) resize.observe(page);
    if (table.parentElement) resize.observe(table.parentElement);
    const bar = document.querySelector('.topbar');
    if (bar) resize.observe(bar);
    const mutations = new MutationObserver(schedule);
    mutations.observe(page ?? table, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'data-melted', 'data-open', 'aria-sort'] });
    mutations.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-layout', 'data-color-scheme', 'data-appearance'] });
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('transitionend', schedule);
    measure();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      resize.disconnect();
      mutations.disconnect();
      document.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('transitionend', schedule);
    };
  }, []);

  return <>
    <thead {...props} ref={headRef}>{children}</thead>
    {dock && createPortal(
      <div className="library-column-melt" style={{ top: dock.top, left: dock.left, width: dock.width }}>
        <div className={dock.contextClass}>
          <table className={`${dock.tableClass} library-column-melt__table`}
            aria-label="Sticky library column headers"
            style={{ width: dock.tableWidth, transform: `translateX(${dock.tableLeft}px)` }}>
            <colgroup>{dock.columns.map((width, index) => <col key={index} style={{ width }} />)}</colgroup>
            <thead {...props}>{children}</thead>
          </table>
        </div>
      </div>, document.body,
    )}
  </>;
}
